import React from "react";
import {
  Box,
  Grid,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  RadioGroup as MuiRadioGroup,
  FormControlLabel as MuiFormControlLabel,
  Radio as MuiRadio,
  Tooltip,
} from "@mui/material";
import AccordionSummary from "@mui/material/AccordionSummary";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FactoryIcon from "@mui/icons-material/Factory";
import LocalHotelIcon from "@mui/icons-material/LocalHotel";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import { Label } from "@/components/Label";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import { Checkbox, CheckboxGroup } from "@/components/Checkbox";
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormSetValue,
  type UseFormWatch,
} from "react-hook-form";
import { DateRangePicker } from "@/components/DatePicker";
import {
  preventNonNumericValueWhenKeyDown,
  preventNonNumericValueWhenPaste,
} from "@/utils/utils";
import { type ISelectObject } from "@/types/select";
import { type FormData } from "../types/formTypes";
import { MultiSelect } from "@/components/Select";
import { RangeInput } from "./RangeInput";
import { AreaUsePurposeDataSourceToggle } from "@/components/Toggle";

// 不動産種別のマップ
export const REAL_ESTATE_TYPE_MAP = {
  land: { label: "土地", value: "land" },
  kubunBuilding: { label: "区分建物", value: "kubun_building" },
  building: { label: "建物", value: "building" },
};

// 構造
export const BUILDING_VARIANT_OPTIONS: ISelectObject[] = [
  { value: "1", label: "木造" },
  { value: "2", label: "CB（コンクリートブロック）" },
  { value: "3", label: "CFT（コンクリート充填鋼管）" },
  { value: "4", label: "鉄骨造" },
  { value: "13", label: "軽量鉄骨造" },
  { value: "5", label: "RC（鉄筋コンクリート）" },
  { value: "6", label: "SRC（鉄骨鉄筋コンクリート）" },
  { value: "7", label: "PC（プレキャストコンクリート）" },
  { value: "8", label: "HPC（ハードプレキャストコンクリート）" },
  { value: "9", label: "AL（アルミ）" },
  { value: "10", label: "ALC（軽量気泡コンクリート）" },
  { value: "11", label: "鉄筋ブロック造" },
  { value: "12", label: "その他" },
];

// 容積率で入力可能な最小値、最小値を定義する。
const YOUSEKI_RATIO_MIN = 50;
const YOUSEKI_RATIO_MAX = 1300;
// 建ぺい率で入力可能な最小値、最小値を定義する。
const KENPEI_RATIO_MIN = 30;
const KENPEI_RATIO_MAX = 80;

interface BuildingSearchAccordionProps {
  control: Control<FormData>;
  watchedRealEstateType: string;
  setValue: UseFormSetValue<FormData>;
  onBuildDateStartErrorChange?: (error: unknown) => void;
  onBuildDateEndErrorChange?: (error: unknown) => void;
  formState: {
    errors: FieldErrors<FormData>;
  };
  minDate: Date;
  maxDate: Date;
  areaUsePurposeOptions: ISelectObject[];
  hotelCategoryOptions: ISelectObject[];
  dormitoryKindOptions: ISelectObject[];
  hotelSearch: boolean;
  dormitorySearch: boolean;
  industrialFacilitySearch: boolean;
  watch: UseFormWatch<FormData>;
  cityPlanningAreaUsePurposeSearch?: boolean;
}

export const BuildingSearchAccordion: React.FC<
  BuildingSearchAccordionProps
> = ({
  control,
  watchedRealEstateType,
  setValue,
  onBuildDateStartErrorChange,
  onBuildDateEndErrorChange,
  formState,
  minDate,
  maxDate,
  areaUsePurposeOptions,
  hotelCategoryOptions,
  dormitoryKindOptions,
  hotelSearch,
  dormitorySearch,
  industrialFacilitySearch,
  watch,
  cityPlanningAreaUsePurposeSearch = false,
}) => {
  const facilitySearchEnabled =
    hotelSearch || dormitorySearch || industrialFacilitySearch;
  const handleTabChange = (
    _e: React.SyntheticEvent,
    newValue: "building" | "hotel" | "dormitory" | "industrialFacility"
  ): void => {
    if (newValue === "hotel") {
      // ホテルタブへ切り替え: 建物フィールドを全クリア、hotel フラグを立てる
      setValue("buildingType", {
        mansion: false,
        apartment: false,
        building: false,
        jpHotel: false,
        shop: false,
        hotel: true,
        factory: false,
        warehouse: false,
        townhouse: false,
        dormitory: false,
        industrialFacility: false,
        other: false,
      });
      setValue("ownershipType", null);
      setValue("kaokuNumber", { min: null, max: null });
      setValue("propertyName", "");
      setValue("buildingVariant", []);
      setValue("exclusiveArea", { min: null, max: null });
      setValue("buildingAge", { min: null, max: null });
      setValue("buildDateRange", [null, null]);
      setValue("totalFloorArea", { min: null, max: null });
      setValue("groundFloors", { min: null, max: null });
      setValue("undergroundFloors", { min: null, max: null });
      setValue("parkingSpaces", { min: null, max: null });
      setValue("landArea", { min: null, max: null });
      setValue("buildingArea", { min: null, max: null });
      setValue("buildingAreaUsePurpose", []);
      setValue("buildingPrice", { min: null, max: null });
      setValue("buildingWalk", null);
      setValue("buildingYousekiRatio", { min: null, max: null });
      setValue("buildingKenpeiRatio", { min: null, max: null });
      setValue("dormitoryName", "");
      setValue("dormitoryKind", []);
      setValue("industrialFacilityName", "");
    } else if (newValue === "dormitory") {
      // 寮・社宅タブへ切り替え: 建物フィールドを全クリア、dormitory フラグを立てる
      setValue("buildingType", {
        mansion: false,
        apartment: false,
        building: false,
        jpHotel: false,
        shop: false,
        hotel: false,
        factory: false,
        warehouse: false,
        townhouse: false,
        dormitory: true,
        industrialFacility: false,
        other: false,
      });
      setValue("ownershipType", null);
      setValue("kaokuNumber", { min: null, max: null });
      setValue("propertyName", "");
      setValue("buildingVariant", []);
      setValue("exclusiveArea", { min: null, max: null });
      setValue("buildingAge", { min: null, max: null });
      setValue("buildDateRange", [null, null]);
      setValue("totalFloorArea", { min: null, max: null });
      setValue("groundFloors", { min: null, max: null });
      setValue("undergroundFloors", { min: null, max: null });
      setValue("parkingSpaces", { min: null, max: null });
      setValue("landArea", { min: null, max: null });
      setValue("buildingArea", { min: null, max: null });
      setValue("hotelName", "");
      setValue("hotelCategory", []);
      setValue("hotelEstimateAcreage", { min: null, max: null });
      setValue("hotelRoomCount", { min: null, max: null });
      setValue("buildingAreaUsePurpose", []);
      setValue("buildingPrice", { min: null, max: null });
      setValue("buildingWalk", null);
      setValue("buildingYousekiRatio", { min: null, max: null });
      setValue("buildingKenpeiRatio", { min: null, max: null });
      setValue("industrialFacilityName", "");
    } else if (newValue === "industrialFacility") {
      // 工場/倉庫タブへ切り替え: 建物フィールドを全クリア、industrialFacility フラグを立てる
      setValue("buildingType", {
        mansion: false,
        apartment: false,
        building: false,
        jpHotel: false,
        shop: false,
        hotel: false,
        factory: false,
        warehouse: false,
        townhouse: false,
        dormitory: false,
        industrialFacility: true,
        other: false,
      });
      setValue("ownershipType", null);
      setValue("kaokuNumber", { min: null, max: null });
      setValue("propertyName", "");
      setValue("buildingVariant", []);
      setValue("exclusiveArea", { min: null, max: null });
      setValue("buildingAge", { min: null, max: null });
      setValue("buildDateRange", [null, null]);
      setValue("totalFloorArea", { min: null, max: null });
      setValue("groundFloors", { min: null, max: null });
      setValue("undergroundFloors", { min: null, max: null });
      setValue("parkingSpaces", { min: null, max: null });
      setValue("landArea", { min: null, max: null });
      setValue("buildingArea", { min: null, max: null });
      setValue("hotelName", "");
      setValue("hotelCategory", []);
      setValue("hotelEstimateAcreage", { min: null, max: null });
      setValue("hotelRoomCount", { min: null, max: null });
      setValue("buildingAreaUsePurpose", []);
      setValue("buildingPrice", { min: null, max: null });
      setValue("buildingWalk", null);
      setValue("buildingYousekiRatio", { min: null, max: null });
      setValue("buildingKenpeiRatio", { min: null, max: null });
      setValue("dormitoryName", "");
      setValue("dormitoryKind", []);
    } else {
      // 建物タブへ切り替え: ホテルフィールドを全クリア、hotel フラグを戻す
      setValue("buildingType", {
        mansion: false,
        apartment: false,
        building: false,
        jpHotel: false,
        shop: false,
        hotel: false,
        factory: false,
        warehouse: false,
        townhouse: false,
        dormitory: false,
        industrialFacility: false,
        other: false,
      });
      setValue("hotelName", "");
      setValue("hotelCategory", []);
      setValue("hotelEstimateAcreage", { min: null, max: null });
      setValue("hotelRoomCount", { min: null, max: null });
      setValue("buildingAreaUsePurpose", []);
      setValue("buildingPrice", { min: null, max: null });
      setValue("buildingWalk", null);
      setValue("buildingYousekiRatio", { min: null, max: null });
      setValue("buildingKenpeiRatio", { min: null, max: null });
      setValue("dormitoryName", "");
      setValue("dormitoryKind", []);
      setValue("industrialFacilityName", "");
    }
  };

  // 建物種別の選択状態を監視
  const watchedBuildingType = watch("buildingType");
  const isHotelTabActive = hotelSearch && watchedBuildingType.hotel;
  const isDormitoryTabActive = dormitorySearch && watchedBuildingType.dormitory;
  const isIndustrialFacilityTabActive =
    industrialFacilitySearch && watchedBuildingType.industrialFacility;
  const isBuildingTypeSelected = Object.values(watchedBuildingType).some(
    (v) => v
  );
  // 所有形態の選択状態を監視
  const watchedOwnershipType = watch("ownershipType");
  // 家屋番号数を表示する条件: 建物種別が未選択、または所有形態が指定なしの場合
  const isKaokuNumberVisible =
    !isBuildingTypeSelected || watchedOwnershipType === null;

  return (
    <Accordion
      expanded={watchedRealEstateType === REAL_ESTATE_TYPE_MAP.building.value}
      onChange={(_, _expanded) => {
        setValue("realEstateType", REAL_ESTATE_TYPE_MAP.building.value, {
          shouldValidate: true,
        });
      }}
      square
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        py: 1,
        px: 2,
        mt: 1,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
        <Box
          component="label"
          sx={{ display: "flex", alignItems: "center", width: "100%" }}
        >
          <MuiRadio
            checked={
              watchedRealEstateType === REAL_ESTATE_TYPE_MAP.building.value
            }
            onChange={() => {
              setValue("realEstateType", REAL_ESTATE_TYPE_MAP.building.value, {
                shouldValidate: true,
              });
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Box sx={{ flex: "0 0 auto", mr: 5 }}>
              <Typography variant="subtitle1" sx={{ color: "text.primary" }}>
                建物の条件から探す
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: "left" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                マンション、アパート等の建物種別や建物名、階数など
                <br />
                区分建物・建物に関連する内容を検索条件に含めることができます
              </Typography>
            </Box>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        {facilitySearchEnabled && (
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              width: "fit-content",
            }}
          >
            <Tabs
              value={
                isHotelTabActive
                  ? "hotel"
                  : isDormitoryTabActive
                  ? "dormitory"
                  : isIndustrialFacilityTabActive
                  ? "industrialFacility"
                  : "building"
              }
              onChange={handleTabChange}
              TabIndicatorProps={{
                sx: { height: 3, transform: "scaleX(0.8)" },
              }}
            >
              <Tab
                value="building"
                icon={<ApartmentIcon />}
                iconPosition="start"
                label="建物(マンション・アパート・ビル等)を探す"
              />
              {hotelSearch && (
                <Tab
                  value="hotel"
                  icon={<LocalHotelIcon />}
                  iconPosition="start"
                  label="ホテルを探す"
                />
              )}
              {dormitorySearch && (
                <Tab
                  value="dormitory"
                  icon={<HomeWorkIcon />}
                  iconPosition="start"
                  label="寮・社宅を探す"
                />
              )}
              {industrialFacilitySearch && (
                <Tab
                  value="industrialFacility"
                  icon={<FactoryIcon />}
                  iconPosition="start"
                  label="工場/倉庫を探す"
                />
              )}
            </Tabs>
          </Box>
        )}
        {(!facilitySearchEnabled ||
          (!isHotelTabActive &&
            !isDormitoryTabActive &&
            !isIndustrialFacilityTabActive)) && (
          <>
            <Box
              sx={{
                mx: 2,
                mt: 2,
                mb: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Grid container>
                <Grid item xs={12}>
                  <CheckboxGroup label="建物種別">
                    <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                      <Controller
                        control={control}
                        name={"buildingType"}
                        render={({ field: { value, onChange } }) => {
                          const buildingTypeOptions = [
                            { key: "mansion", label: "マンション" },
                            { key: "apartment", label: "アパート" },
                            { key: "building", label: "ビル" },
                            { key: "other", label: "その他" },
                          ] as const;
                          return (
                            <>
                              {buildingTypeOptions.map((option) => (
                                <Checkbox
                                  key={option.key}
                                  label={option.label}
                                  checked={value[option.key]}
                                  onChange={(checked) => {
                                    const newValue = {
                                      ...value,
                                      [option.key]: checked,
                                    };
                                    onChange(newValue);

                                    // 建物種別の選択状態に応じてownershipTypeを連動
                                    const anySelected = Object.values(
                                      newValue
                                    ).some((v) => v);
                                    if (anySelected) {
                                      // 建物種別が選択されたら家屋番号数をクリア
                                      setValue("kaokuNumber", {
                                        min: null,
                                        max: null,
                                      });
                                    } else {
                                      // 建物種別が全て解除されたらownershipTypeもクリア
                                      setValue("ownershipType", null);
                                    }
                                  }}
                                />
                              ))}
                            </>
                          );
                        }}
                      />
                    </Box>
                  </CheckboxGroup>
                  {/* 建物種別が1つ以上選択されている場合のみ「所有形態」を表示 */}
                  {isBuildingTypeSelected && (
                    <Box sx={{ mt: 2 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Label sx={{ mb: 0 }}>所有形態</Label>
                        <Tooltip
                          title={
                            <span>
                              一棟はR.E.DATA独自の定義です。詳細は
                              <a
                                href="https://www.notion.so/trustart/10-18414754f2df80e8954acd41b30740cf?source=copy_link#18a14754f2df80fabaa7d74649b495b6"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#90caf9" }}
                              >
                                マニュアル
                              </a>
                              をご確認ください
                            </span>
                          }
                        >
                          <HelpOutlineIcon
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                              cursor: "help",
                            }}
                          />
                        </Tooltip>
                      </Box>
                      <Controller
                        control={control}
                        name="ownershipType"
                        render={({ field: { value, onChange } }) => (
                          <MuiRadioGroup
                            row
                            value={value ?? ""}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              onChange(newValue === "" ? null : newValue);
                              // 一棟または分譲を選択したら家屋番号数をクリア
                              if (newValue !== "") {
                                setValue("kaokuNumber", {
                                  min: null,
                                  max: null,
                                });
                              }
                            }}
                          >
                            <MuiFormControlLabel
                              value=""
                              control={<MuiRadio />}
                              label="指定なし"
                            />
                            <MuiFormControlLabel
                              value="ittou"
                              control={<MuiRadio />}
                              label="一棟"
                            />
                            <MuiFormControlLabel
                              value="bunjou"
                              control={<MuiRadio />}
                              label="分譲"
                            />
                          </MuiRadioGroup>
                        )}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
            {isKaokuNumberVisible && (
              <Box sx={{ mx: 2, mb: 2 }}>
                <Label>家屋番号数（対象建物下の地番上の家屋番号数）</Label>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Controller
                    control={control}
                    name="kaokuNumber"
                    render={({ field: { value, onChange }, fieldState }) => (
                      <TextField
                        error={Boolean(
                          formState.errors.kaokuNumber?.min ?? fieldState.error
                        )}
                        helperText={
                          formState.errors.kaokuNumber?.min?.message ??
                          fieldState.error?.message
                        }
                        value={
                          value.min === null || value.min === 0 ? "" : value.min
                        }
                        label="以上"
                        onChange={(e) => {
                          onChange({
                            min:
                              e.target.value === ""
                                ? null
                                : Number(e.target.value),
                            max: value.max,
                          });
                        }}
                        onKeyDown={preventNonNumericValueWhenKeyDown}
                        onPaste={preventNonNumericValueWhenPaste}
                        sx={{ width: "250px", mr: 1 }}
                        inputProps={{ min: 0, type: "number", step: "1" }}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="kaokuNumber"
                    render={({ field: { value, onChange }, fieldState }) => (
                      <TextField
                        error={Boolean(
                          formState.errors.kaokuNumber?.max ?? fieldState.error
                        )}
                        helperText={
                          formState.errors.kaokuNumber?.max?.message ??
                          fieldState.error?.message
                        }
                        value={
                          value.max === null || value.max === 0 ? "" : value.max
                        }
                        label="以下"
                        onChange={(e) => {
                          onChange({
                            min: value.min,
                            max:
                              e.target.value === ""
                                ? null
                                : Number(e.target.value),
                          });
                        }}
                        onKeyDown={preventNonNumericValueWhenKeyDown}
                        onPaste={preventNonNumericValueWhenPaste}
                        sx={{ width: "250px" }}
                        inputProps={{ min: 0, type: "number", step: "1" }}
                      />
                    )}
                  />
                </Box>
              </Box>
            )}
            <Box sx={{ mx: 2, mb: 2 }}>
              <Label>建物名</Label>
              <Controller
                control={control}
                name="propertyName"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    placeholder="建物名"
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    sx={{ width: "250px" }}
                  />
                )}
              />
            </Box>
            <Box sx={{ mx: 2, mb: 2 }}>
              <Label>構造</Label>
              <Controller
                control={control}
                name="buildingVariant"
                render={({ field: { value, onChange }, fieldState }) => (
                  <MultiSelect
                    error={Boolean(fieldState.error)}
                    formHelperText={fieldState.error?.message}
                    label="構造"
                    value={value.map((value) => value.toString())}
                    onChange={(value) => {
                      onChange(value.map((value) => Number(value)));
                    }}
                    options={BUILDING_VARIANT_OPTIONS}
                  />
                )}
              />
            </Box>
            <Box sx={{ mx: 2, mb: 2 }}>
              <RangeInput
                control={control}
                name="exclusiveArea"
                label="専有面積"
                unit="㎡"
                errors={formState.errors}
                inputProps={{ min: 0, type: "number", step: "1" }}
              />
            </Box>
            <Box sx={{ mx: 2, mb: 2 }}>
              <Label>築年数</Label>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Controller
                  control={control}
                  name={"buildingAge"}
                  render={({ field: { value, onChange }, fieldState }) => (
                    <TextField
                      error={Boolean(
                        formState.errors.buildingAge?.min ?? fieldState.error
                      )}
                      helperText={
                        formState.errors.buildingAge?.min?.message ??
                        fieldState.error?.message
                      }
                      value={
                        value.min === null || value.min === 0 ? "" : value.min
                      }
                      label="年以上"
                      onChange={(e) => {
                        onChange({
                          min:
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                          max: value.max,
                        });
                      }}
                      onKeyDown={preventNonNumericValueWhenKeyDown}
                      onPaste={preventNonNumericValueWhenPaste}
                      sx={{ width: "250px", mr: 1 }}
                      inputProps={{ min: 0, type: "number", step: "10" }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={"buildingAge"}
                  render={({ field: { value, onChange }, fieldState }) => (
                    <TextField
                      error={Boolean(
                        formState.errors.buildingAge?.max ?? fieldState.error
                      )}
                      helperText={
                        formState.errors.buildingAge?.max?.message ??
                        fieldState.error?.message
                      }
                      value={
                        value.max === null || value.max === 0 ? "" : value.max
                      }
                      label="年以下"
                      onChange={(e) => {
                        onChange({
                          min: value.min,
                          max:
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                        });
                      }}
                      onKeyDown={preventNonNumericValueWhenKeyDown}
                      onPaste={preventNonNumericValueWhenPaste}
                      sx={{ width: "250px" }}
                      inputProps={{ min: 0, type: "number", step: "10" }}
                    />
                  )}
                />
              </Box>
            </Box>
            <Box sx={{ mx: 2, mb: 2 }}>
              <Label>築年月</Label>
              <Controller
                control={control}
                name={"buildDateRange"}
                render={({ field: { value, onChange }, fieldState }) => {
                  const rangeErrors = fieldState.error as
                    | Array<{ message?: string } | undefined>
                    | undefined;
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <DateRangePicker
                        startDateLabel="以降"
                        startDate={value[0]}
                        onChangeStartDate={(date) => {
                          onChange([date, value[1]]);
                        }}
                        onStartDateErrorChange={onBuildDateStartErrorChange}
                        endDateLabel="以前"
                        endDate={value[1]}
                        minDate={minDate}
                        maxDate={maxDate}
                        onChangeEndDate={(date) => {
                          onChange([value[0], date]);
                        }}
                        onEndDateErrorChange={onBuildDateEndErrorChange}
                        inputFormat={"yyyy/MM"}
                        mask={"____/__"}
                        views={["year", "month"]}
                        errorStart={rangeErrors?.[0]?.message}
                        errorEnd={rangeErrors?.[1]?.message}
                      />
                    </Box>
                  );
                }}
              />
            </Box>
            <Box sx={{ mx: 2, mb: 2 }}>
              <RangeInput
                control={control}
                name="totalFloorArea"
                label="延べ床面積"
                unit="㎡"
                errors={formState.errors}
                inputProps={{ min: 0, type: "number", step: "1" }}
              />
            </Box>
            <Box sx={{ mx: 2, mb: 2 }}>
              <RangeInput
                control={control}
                name="groundFloors"
                label="地上階"
                errors={formState.errors}
                inputProps={{ min: 0, type: "number", step: "1" }}
              />
            </Box>
            <Box sx={{ mx: 2, mb: 2 }}>
              <RangeInput
                control={control}
                name="undergroundFloors"
                label="地下階"
                errors={formState.errors}
                inputProps={{ min: 0, type: "number", step: "1" }}
              />
            </Box>
            <Box sx={{ mx: 2, mb: 2 }}>
              <RangeInput
                control={control}
                name="parkingSpaces"
                label="駐車場数"
                errors={formState.errors}
                inputProps={{ min: 0, type: "number", step: "1" }}
              />
            </Box>
            <Box sx={{ mx: 2, mb: 2 }}>
              <RangeInput
                control={control}
                name="landArea"
                label="土地面積"
                unit="㎡"
                errors={formState.errors}
                inputProps={{ min: 0, type: "number", step: "1" }}
              />
            </Box>
            <Box sx={{ mx: 2, mb: 2 }}>
              <RangeInput
                control={control}
                name="buildingArea"
                label="建物面積"
                unit="㎡"
                errors={formState.errors}
                inputProps={{ min: 0, type: "number", step: "1" }}
              />
            </Box>
          </>
        )}
        {hotelSearch && watchedBuildingType.hotel && (
          <>
            <Box sx={{ mx: 2, mt: 2, mb: 2 }}>
              <Label>ホテル名</Label>
              <Controller
                control={control}
                name="hotelName"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    placeholder="ホテル名"
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    sx={{ width: "250px" }}
                  />
                )}
              />
            </Box>
            <Box sx={{ mx: 2, mb: 2 }}>
              <Label>ホテル形態</Label>
              <Controller
                control={control}
                name="hotelCategory"
                render={({ field: { value, onChange }, fieldState }) => (
                  <MultiSelect
                    error={Boolean(fieldState.error)}
                    formHelperText={fieldState.error?.message}
                    label="ホテル形態"
                    value={value.map((value) => value.toString())}
                    onChange={(value) => {
                      onChange(value.map((value) => Number(value)));
                    }}
                    options={hotelCategoryOptions}
                  />
                )}
              />
            </Box>
            <Box sx={{ mx: 2, mb: 2 }}>
              <RangeInput
                control={control}
                name="hotelEstimateAcreage"
                label="ホテル 推計地積"
                unit="㎡"
                errors={formState.errors}
                inputProps={{ min: 0, type: "number", step: "10" }}
              />
            </Box>
            <Box sx={{ mx: 2, mb: 2 }}>
              <RangeInput
                control={control}
                name="hotelRoomCount"
                label="ホテル 部屋数"
                errors={formState.errors}
                inputProps={{ min: 0, type: "number", step: "1" }}
              />
            </Box>
          </>
        )}
        {dormitorySearch && watchedBuildingType.dormitory && (
          <>
            <Box sx={{ mx: 2, mt: 2, mb: 2 }}>
              <Label>寮・社宅名</Label>
              <Controller
                control={control}
                name="dormitoryName"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    placeholder="寮・社宅名"
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    sx={{ width: "250px" }}
                  />
                )}
              />
            </Box>
            <Box sx={{ mx: 2, mb: 2 }}>
              <Label>種別</Label>
              <Controller
                control={control}
                name="dormitoryKind"
                render={({ field: { value, onChange }, fieldState }) => (
                  <MultiSelect
                    error={Boolean(fieldState.error)}
                    formHelperText={fieldState.error?.message}
                    label="種別"
                    value={value}
                    onChange={onChange}
                    options={dormitoryKindOptions}
                  />
                )}
              />
            </Box>
          </>
        )}
        {industrialFacilitySearch && watchedBuildingType.industrialFacility && (
          <Box sx={{ mx: 2, mt: 2, mb: 2 }}>
            <Label>工場/倉庫名</Label>
            <Controller
              control={control}
              name="industrialFacilityName"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  placeholder="工場/倉庫名"
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  sx={{ width: "250px" }}
                />
              )}
            />
          </Box>
        )}
        <Box sx={{ mx: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Label>用途地域</Label>
            {cityPlanningAreaUsePurposeSearch && (
              <Controller
                control={control}
                name="areaUsePurposeDataSource"
                render={({ field: { value, onChange } }) => (
                  <AreaUsePurposeDataSourceToggle
                    value={value}
                    onChange={(_event, newValue) => {
                      if (newValue !== null) {
                        onChange(newValue);
                      }
                    }}
                  />
                )}
              />
            )}
          </Box>
          <Controller
            control={control}
            name="buildingAreaUsePurpose"
            render={({ field: { value, onChange }, fieldState }) => {
              return (
                <MultiSelect
                  error={Boolean(fieldState.error)}
                  formHelperText={fieldState.error?.message}
                  label="用途地域"
                  value={value.map((value) => value.toString())}
                  onChange={(value) => {
                    onChange(value.map((value) => Number(value)));
                  }}
                  options={areaUsePurposeOptions}
                  maxSelect={5}
                />
              );
            }}
          />
        </Box>
        <Box sx={{ mx: 2, mb: 2 }}>
          <RangeInput
            control={control}
            name="buildingYousekiRatio"
            label="容積率"
            unit="%"
            errors={formState.errors}
            inputProps={{
              min: YOUSEKI_RATIO_MIN,
              max: YOUSEKI_RATIO_MAX,
              type: "number",
              step: "10",
            }}
          />
        </Box>
        <Box sx={{ mx: 2, mb: 2 }}>
          <RangeInput
            control={control}
            name="buildingKenpeiRatio"
            label="建ぺい率"
            unit="%"
            errors={formState.errors}
            inputProps={{
              min: KENPEI_RATIO_MIN,
              max: KENPEI_RATIO_MAX,
              type: "number",
              step: "10",
            }}
          />
        </Box>
        <Box sx={{ mx: 2, mb: 2 }}>
          <RangeInput
            control={control}
            name="buildingPrice"
            label="公示価格"
            unit="円/㎡"
            errors={formState.errors}
            inputProps={{ min: 0, type: "number", step: "10" }}
          />
        </Box>
        <Box sx={{ mx: 2, mb: 2 }}>
          <Label>最寄駅からの徒歩時間</Label>
          <Controller
            control={control}
            name="buildingWalk"
            render={({ field: { value, onChange } }) => {
              return (
                <Select
                  value={value === null ? -1 : value}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  sx={{ width: "250px", minHeight: "56px" }}
                  variant="outlined"
                >
                  <MenuItem key={-1} value={-1}>
                    指定なし
                  </MenuItem>
                  {[...Array(60).keys()]
                    .filter((val) => val >= 1)
                    .map((val) => (
                      <MenuItem key={val} value={val}>
                        {val}分以内
                      </MenuItem>
                    ))}
                  <MenuItem key={60} value={60}>
                    60分以上
                  </MenuItem>
                </Select>
              );
            }}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
